import type { Direction } from "@agency/shared";
import type Phaser from "phaser";
import { AvatarView } from "./AvatarView.js";

interface Snapshot {
  t: number;
  x: number;
  y: number;
  dir: Direction;
  moving: boolean;
}

const RENDER_DELAY_MS = 120;
const TELEPORT_PX = 64;
/** Expected spacing of server samples; used to seed movement after a pause. */
const SAMPLE_INTERVAL_MS = 50;
/** A gap longer than this is a real pause, not network jitter. */
const PAUSE_MS = 250;

/** A remote player rendered by interpolating ~100 ms in the past. */
export class RemotePlayer {
  readonly view: AvatarView;
  private buffer: Snapshot[] = [];
  private x: number;
  private y: number;

  constructor(scene: Phaser.Scene, avatarId: string, nickname: string, x: number, y: number) {
    this.x = x;
    this.y = y;
    this.view = new AvatarView(scene, x, y, avatarId, nickname, false);
  }

  /**
   * Record the latest server state. Call freely (e.g. every frame): only
   * actual changes become samples, timestamped when they were first seen.
   */
  push(now: number, x: number, y: number, dir: Direction, moving: boolean): void {
    const last = this.buffer[this.buffer.length - 1];
    if (last && last.x === x && last.y === y && last.dir === dir && last.moving === moving) return;
    if (last && Math.hypot(x - last.x, y - last.y) > TELEPORT_PX) {
      this.buffer.length = 0;
      this.x = x;
      this.y = y;
      this.view.setPosition(x, y);
    } else if (last && now - last.t > PAUSE_MS) {
      // Resuming after a pause: start from where they stood one interval ago,
      // rather than interpolating across the whole pause (which teleports).
      this.buffer.push({ ...last, t: now - SAMPLE_INTERVAL_MS });
    }
    this.buffer.push({ t: now, x, y, dir, moving });
    while (this.buffer.length > 2 && now - this.buffer[0]!.t > 1000) this.buffer.shift();
  }

  update(now: number, activity: string, connected: boolean): void {
    const renderTime = now - RENDER_DELAY_MS;
    let prev: Snapshot | undefined;
    let next: Snapshot | undefined;
    for (let i = 0; i < this.buffer.length; i++) {
      const s = this.buffer[i]!;
      if (s.t <= renderTime) prev = s;
      if (s.t >= renderTime) {
        next = s;
        break;
      }
    }

    let dir: Direction = this.buffer.at(-1)?.dir ?? 0;
    if (prev && next && next.t !== prev.t) {
      const a = (renderTime - prev.t) / (next.t - prev.t);
      this.x = prev.x + (next.x - prev.x) * a;
      this.y = prev.y + (next.y - prev.y) * a;
      dir = next.dir;
    } else if (next) {
      this.x = next.x;
      this.y = next.y;
      dir = next.dir;
    } else if (prev) {
      this.x = prev.x;
      this.y = prev.y;
    }

    this.view.setPosition(this.x, this.y);
    this.view.setDirection(dir, next?.moving ?? prev?.moving ?? false);
    this.view.setActivity(connected ? activity : "idle");
    this.view.setConnected(connected);
  }

  destroy(): void {
    this.view.destroy();
  }
}
